import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';
import { flushSync } from 'react-dom';
import type { CustomChartSize } from '@/entities/dashboards';
import type { ChartSizeChange } from '../../model/useDashboardPresetEditor';
import { clampChartHeight, nearestChartSize } from '../DashboardPresetGrid/chartLayout';

/** Граница карточки, за которую тянут: правая задаёт ширину, нижняя — высоту. */
export type ChartResizeEdge = 'width' | 'height';

/** Начало растягивания: от этих величин отсчитывается новый размер диаграммы. */
type ResizeStart = {
  edge: ChartResizeEdge;
  pointerX: number;
  pointerY: number;
  cardWidth: number;
  cardHeight: number;
  gridWidth: number;
};

type Params = {
  /** Ячейка диаграммы на сетке — на ней показана высота по ходу растягивания */
  cellRef: RefObject<HTMLDivElement | null>;
  /** Карточка диаграммы — от её размеров отсчитывается растягивание */
  cardRef: RefObject<HTMLDivElement | null>;
  /** Доля ширины диаграммы в составе дашборда */
  size: CustomChartSize;
  /** Заданная высота диаграммы в составе; не задана — высота подбирается по содержимому */
  height?: number | null;
  /** Ширина сетки дашборда: по ней доля ширины диаграммы считается заново */
  getGridWidth: () => number;
  onResize: (change: ChartSizeChange) => void;
};

/** Основная кнопка мыши в маске зажатых кнопок указателя. */
const PRIMARY_BUTTON_MASK = 1;

/**
 * Растягивание границ диаграммы на сетке.
 *
 * Ширина сразу встаёт на ближайшую долю каталога, а не на любое число пикселей: доли делят
 * сетку нацело, и промежуточная ширина оставила бы в ряду дыру. Размер считается от размеров
 * карточки на начало растягивания, поэтому диаграмма не «убегает» от курсора, перестраиваясь
 * под каждую новую долю.
 *
 * Размер под курсором считается раз в кадр, сколько бы движений ни пришло между кадрами, и состав
 * дашборда его получает не на каждом движении: смена состава перерисовывает сетку, и на медленном
 * компьютере граница отставала бы от курсора рывками. Доля ширины уходит в состав на смене ступени —
 * от неё зависит раскладка строки. Высота показывается прямо на ячейке и уходит в состав на
 * отпускании; диаграмма без заданной высоты получает её составом сразу, на первом движении, — с ней
 * содержимое раскладывается под заданную высоту.
 *
 * Растягивание — жест: оно длится, пока зажата кнопка, и ведётся событиями окна, а не самой ручки.
 * Ручка за время жеста может потерять указатель — диаграмма от растягивания переезжает в другую
 * строку, курсор уходит за окно, — и отпускание до неё тогда не доходит. Окно же получает и
 * отпускание где угодно, и движение уже без зажатой кнопки, если отпускание пришлось вне окна;
 * потеря окном фокуса жест тоже завершает. Размер остаётся последним показанным.
 */
export const useChartCardResize = ({ cellRef, cardRef, size, height, getGridWidth, onResize }: Params) => {
  const [isResizing, setIsResizing] = useState(false);
  // Завершение идущего жеста: снимает подписки окна, а с `isApplied` — отдаёт составу последний
  // показанный размер; жеста нет — `null`
  const finishRef = useRef<((isApplied: boolean) => void) | null>(null);
  // Размер в составе на сейчас: жест отсчитывается от него, не пересоздавая обработчик нажатия
  const sizeRef = useRef({ size, height: height ?? null });
  sizeRef.current = { size, height: height ?? null };

  // Ушедшая со страницы диаграмма не оставляет за собой подписок окна и размера ей уже не нужно
  useEffect(() => () => finishRef.current?.(false), []);

  const handlePointerDown = useCallback(
    (edge: ChartResizeEdge, event: ReactPointerEvent<HTMLDivElement>) => {
      const card = cardRef.current;
      if (!card || event.button !== 0) return;
      finishRef.current?.(true);

      const { width, height: cardHeight } = card.getBoundingClientRect();
      const start: ResizeStart = {
        edge,
        pointerX: event.clientX,
        pointerY: event.clientY,
        cardWidth: width,
        cardHeight,
        gridWidth: getGridWidth(),
      };
      const { pointerId } = event;
      // Размер, который уже знает состав, и показанная высота
      const committed = { ...sizeRef.current };
      let shownHeight = committed.height;
      let pointer = { x: event.clientX, y: event.clientY };
      let frame: number | null = null;

      const showFrame = () => {
        frame = null;
        if (start.edge === 'width') {
          const nextSize = nearestChartSize(start.cardWidth + pointer.x - start.pointerX, start.gridWidth);
          if (nextSize === committed.size) return;
          committed.size = nextSize;
          onResize({ size: nextSize });
          return;
        }
        const nextHeight = clampChartHeight(start.cardHeight + pointer.y - start.pointerY);
        if (nextHeight === shownHeight) return;
        shownHeight = nextHeight;
        if (committed.height === null) {
          committed.height = nextHeight;
          // Сразу, в этом же кадре: высота, показанная на ячейке следующими кадрами, не должна
          // затереться запоздалой перерисовкой с первой
          flushSync(() => onResize({ height: nextHeight }));
          return;
        }
        const cell = cellRef.current;
        if (cell) cell.style.height = `${nextHeight}px`;
      };

      const handleMove = (moveEvent: PointerEvent) => {
        if (moveEvent.pointerId !== pointerId) return;
        if ((moveEvent.buttons & PRIMARY_BUTTON_MASK) === 0) {
          finish(true);
          return;
        }
        pointer = { x: moveEvent.clientX, y: moveEvent.clientY };
        frame ??= requestAnimationFrame(showFrame);
      };
      const handleEnd = (endEvent: PointerEvent) => {
        if (endEvent.pointerId === pointerId) finish(true);
      };
      const handleBlur = () => finish(true);
      const finish = (isApplied: boolean) => {
        if (frame !== null) {
          cancelAnimationFrame(frame);
          // Движение, не дождавшееся кадра, — тоже часть жеста: итог — последняя точка курсора
          if (isApplied) showFrame();
        }
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleEnd);
        window.removeEventListener('pointercancel', handleEnd);
        window.removeEventListener('blur', handleBlur);
        finishRef.current = null;
        if (!isApplied) return;
        if (shownHeight !== committed.height) onResize({ height: shownHeight });
        setIsResizing(false);
      };

      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleEnd);
      window.addEventListener('pointercancel', handleEnd);
      window.addEventListener('blur', handleBlur);
      finishRef.current = finish;
      // Нажатие не выделяет текст под ручкой и не уводит фокус
      event.preventDefault();
      setIsResizing(true);
    },
    [cellRef, cardRef, getGridWidth, onResize],
  );

  return { isResizing, handlePointerDown };
};
